import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { ListingTypeOptions } from "@/utils/productTypes";
import {
  type ListingSortOptions,
  sortOptions,
  typeOptions,
} from "pages/root/shop/listingFilterOptions";

const DEFAULT_SEARCH_PARAMS = {
  areas: "",
  concerns: "",
  sort: "featured",
  type: "",
} as const;

const isListingSortOption = (value: string | null): value is ListingSortOptions =>
  value != null && sortOptions.has(value as ListingSortOptions);

const isListingType = (value: string | null): value is ListingTypeOptions =>
  value != null && typeOptions.has(value as ListingTypeOptions);

const joinStrings = (strings: string | null) => (strings === "" ? [] : (strings?.split(",") ?? []));

const stringifyArray = (strings: string[]) => strings.join(",");

/**
 * The filter and sort choices of a shop list, mirrored to the URL so a refresh or a shared link
 * lands on the same view. The results themselves come from Algolia: this hook only holds what the
 * patient picked (including the not-yet-applied picks inside a modal, with a checkpoint to restore
 * on dismiss); the component turns those picks into refinements.
 */
export const useListingFilterAndSort = (treatmentType: ListingTypeOptions | null) => {
  const [searchParams, setSearchParams] = useSearchParams(DEFAULT_SEARCH_PARAMS);

  const typeParam = searchParams.get("type");
  const [type, setType] = useState<ListingTypeOptions | null>(
    typeParam === "" || typeParam == null
      ? treatmentType
      : isListingType(typeParam)
        ? typeParam
        : treatmentType,
  );
  const [concerns, setConcerns] = useState<string[]>(joinStrings(searchParams.get("concerns")));
  const [areas, setAreas] = useState<string[]>(joinStrings(searchParams.get("areas")));
  const sortParam = searchParams.get("sort");
  const [sort, sortBy] = useState<ListingSortOptions>(
    isListingSortOption(sortParam) ? sortParam : "featured",
  );
  const [checkpoints, setCheckpoints] = useState({ type, concerns, areas, sort });

  /** The sort in force for the results — the one in the URL, not a pick still open in the modal. */
  const appliedSort: ListingSortOptions = isListingSortOption(sortParam) ? sortParam : "featured";

  const writeSearchParams = (next: {
    type: ListingTypeOptions | null;
    concerns: string[];
    areas: string[];
    sort: ListingSortOptions;
  }) =>
    setSearchParams({
      areas: stringifyArray(next.areas),
      concerns: stringifyArray(next.concerns),
      sort: next.sort,
      type: next.type ?? "",
    });

  const toggle = (values: string[], value: string) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

  return {
    type,
    /** A pick still open in a modal; the URL is written on "Apply" (`setSearchParams`). */
    setType,
    concerns,
    setConcerns: (concernId: string) => setConcerns(toggle(concerns, concernId)),
    areas,
    setAreas: (areaId: string) => setAreas(toggle(areas, areaId)),
    sort,
    sortBy,
    appliedSort,
    /** Pick a sort and put it in force at once (the desktop rail has no "apply" step). */
    applySort: (next: ListingSortOptions) => {
      sortBy(next);
      writeSearchParams({ type, concerns, areas, sort: next });
    },
    /** Desktop-rail picks: applied and written to the URL in one step, so a reload keeps them. */
    applyType: (next: ListingTypeOptions | null) => {
      setType(next);
      writeSearchParams({ type: next, concerns, areas, sort });
    },
    applyConcern: (concernId: string) => {
      const next = toggle(concerns, concernId);
      setConcerns(next);
      writeSearchParams({ type, concerns: next, areas, sort });
    },
    applyArea: (areaId: string) => {
      const next = toggle(areas, areaId);
      setAreas(next);
      writeSearchParams({ type, concerns, areas: next, sort });
    },
    sortOptions,
    setSearchParams: () => writeSearchParams({ type, concerns, areas, sort }),
    checkpoints,
    saveCheckpoints: () => setCheckpoints({ type, concerns, areas, sort }),
    restore: () => {
      setType(checkpoints.type);
      setConcerns(checkpoints.concerns);
      setAreas(checkpoints.areas);
      sortBy(checkpoints.sort);
    },
    clearConcerns: () => {
      setConcerns([]);
      writeSearchParams({ type, concerns: [], areas, sort });
    },
    clearAreas: () => {
      setAreas([]);
      writeSearchParams({ type, concerns, areas: [], sort });
    },
    /**
     * Every filter back to the page's default, written to the URL once; the sort is kept. `type`
     * goes back to `null` (not `treatmentType`) so the URL loses the explicit param entirely — the
     * page's own default is what re-derives `state.type` as `treatmentType` on the next read.
     */
    reset: () => {
      setType(treatmentType);
      setConcerns([]);
      setAreas([]);
      writeSearchParams({ type: null, concerns: [], areas: [], sort });
    },
  };
};
